import { FC, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button, Dialog, DialogActions, DialogTitle, Typography, Box } from "@mui/material";
import { DialogContent, TextField, MenuItem } from "@mui/material";
import { ShuffleVariant } from "@zesty-io/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { RedirectsCodes, RedirectsTargetType } from "../../../../../../shell/services/types";
import { useDispatch } from "react-redux";
import { notify } from "../../../../../../shell/store/notifications";
import { useRedirectsDialog } from "../../../../../seo/src/app/components/RedirectsDialogProvider";
import { ContentItemProps, TARGET_OPTIONS, getToolTips } from "../../../../../seo/src/app/components/RedirectsDialogProvider/constants";
import { FieldWrapper } from "../../../../../seo/src/app/components/RedirectsDialogProvider/CreateRedirects/FieldWrapper";
import SearchField from "../../../../../seo/src/app/components/RedirectsDialogProvider/CreateRedirects/SearchField";
import PathField from "../../../../../seo/src/app/components/RedirectsDialogProvider/CreateRedirects/PathField";
import { validateUrl } from "utility/validateUrl";
import { searchItems } from "shell/store/content";
export type ContentRedirectModalProps = {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  currentItem: ContentItemProps | null;
  options: ContentItemProps[];
};
export const ContentRedirectModal: FC<ContentRedirectModalProps> = ({
  open,
  onClose,
  loading,
  currentItem,
  options
}) => {
  const {
    t
  } = useTranslation();
  const toolTips = getToolTips(t);
  const dispatch = useDispatch();
  const [targetInternal, setTargetInternal] = useState<ContentItemProps>(null);
  const [targetPath, setTargetPath] = useState<string>("");
  const [targetType, setTargetType] = useState<RedirectsTargetType>("page");
  const target = targetType === "page" ? targetInternal?.ZUID : targetPath;
  const [invalidTarget, setInvalidTarget] = useState<boolean>(false);
  const {
    openErrorDialog,
    isLoading: isRedirectsLoading,
    createRedirects
  } = useRedirectsDialog();
  const isLoading = isRedirectsLoading || loading;
  const urlValidation = useCallback((url: string) => {
    const isValid = validateUrl(url);
    setInvalidTarget(!isValid);
    return isValid;
  }, [setInvalidTarget]);
  const handleCreateRedirect = async () => {
    const requestData = {
      targetType: targetType as RedirectsTargetType,
      target: target,
      paths: [currentItem?.path],
      code: 301 as RedirectsCodes
    };
    const changeResponse = await createRedirects(requestData);
    const errorPaths = changeResponse?.filter((item: any) => item?.status === "error").map((item: any) => ({
      error: item?.message,
      path: item?.path
    }));
    if (!!errorPaths?.length) onClose();
    if (!errorPaths?.length) {
      dispatch(notify({
        kind: "success",
        message: t("content.redirectCreatedOne")
      }));
    } else {
      const resubmitData = {
        ...requestData,
        errors: errorPaths
      };
      openErrorDialog(resubmitData);
    }
    onClose();
  };
  const handleSearch = (term: string) => {
    dispatch(searchItems(term));
  };
  return <Dialog open={open} fullWidth maxWidth="sm" onClose={onClose} slotProps={{
    paper: {
      sx: {
        minHeight: "565px"
      }
    }
  }} data-sentry-element="Dialog" data-sentry-component="ContentRedirectModal" data-sentry-source-file="ContentRedirectModal.tsx">
      <DialogTitle data-sentry-element="DialogTitle" data-sentry-source-file="ContentRedirectModal.tsx">
        <Box sx={{
        bgcolor: "deepOrange.50",
        borderRadius: "100%",
        width: "40px",
        height: "40px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        mb: 1.5
      }} data-sentry-element="Box" data-sentry-source-file="ContentRedirectModal.tsx">
          <ShuffleVariant color="primary" data-sentry-element="ShuffleVariant" data-sentry-source-file="ContentRedirectModal.tsx" />
        </Box>
        <Typography data-cy="RedirectsDeleteDialogHeader" variant="inherit" fontWeight={700} flexGrow={0} flexShrink={0} data-sentry-element="Typography" data-sentry-source-file="ContentRedirectModal.tsx">
          {t("content.redirectThisContentItem")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{
        mt: 1
      }} data-sentry-element="Typography" data-sentry-source-file="ContentRedirectModal.tsx">
          {t("content.redirectInactiveSubHeader")}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{
      p: 0
    }} data-cy="RedirectsDeleteDialog" data-sentry-element="DialogContent" data-sentry-source-file="ContentRedirectModal.tsx">
        <Box width="100%" display="flex" flexDirection="column" justifyContent="flex-start" alignItems="flex-start" rowGap={1.5} py={0} px={2.5} data-sentry-element="Box" data-sentry-source-file="ContentRedirectModal.tsx">
          <Box width="100%" display="flex" flexDirection="column" justifyContent="flex-start" alignItems="space-between" data-sentry-element="Box" data-sentry-source-file="ContentRedirectModal.tsx">
            <Typography variant="body2" color="text.primary" fontWeight={600} noWrap sx={{
            textTransform: "uppercase"
          }} data-sentry-element="Typography" data-sentry-source-file="ContentRedirectModal.tsx">
              {t("content.redirectOldPath")}
            </Typography>
            <Typography variant="body2" color="info.dark" data-sentry-element="Typography" data-sentry-source-file="ContentRedirectModal.tsx">
              {currentItem?.path}
            </Typography>
            <Box display="flex" flexDirection="row" width="100%" my={1} data-sentry-element="Box" data-sentry-source-file="ContentRedirectModal.tsx">
              <ArrowDownwardIcon color="action" fontSize="small" data-sentry-element="ArrowDownwardIcon" data-sentry-source-file="ContentRedirectModal.tsx" />
            </Box>
            <Typography variant="body2" color="text.primary" fontWeight={600} noWrap mb={2.5} sx={{
            textTransform: "uppercase"
          }} data-sentry-element="Typography" data-sentry-source-file="ContentRedirectModal.tsx">
              {t("content.redirectNewPath")}
            </Typography>
            <Box width="100%" display="flex" flexDirection="column" justifyContent="flex-start" alignItems="flex-start" rowGap={2.5} data-sentry-element="Box" data-sentry-source-file="ContentRedirectModal.tsx">
              <FieldWrapper label={t("content.redirectTypeLabel")} tooltip={toolTips.targetType} data-sentry-element="FieldWrapper" data-sentry-source-file="ContentRedirectModal.tsx">
                <TextField data-cy="RedirectsTypeSelector" select defaultValue="page" size="small" fullWidth value={targetType} onChange={(e: any) => {
                setTargetType(e.target.value);
              }} data-sentry-element="TextField" data-sentry-source-file="ContentRedirectModal.tsx">
                  {TARGET_OPTIONS.map(option => <MenuItem key={option.value} value={option.value}>
                      {t(option.label)}
                    </MenuItem>)}
                </TextField>
              </FieldWrapper>
              <FieldWrapper label={t("content.redirectTargetLabel")} tooltip={t("content.redirectFilePathOnlyTooltip")} disabledTooltip={t("content.redirectValueCannotBeModified")} data-sentry-element="FieldWrapper" data-sentry-source-file="ContentRedirectModal.tsx">
                {targetType === "page" ? <SearchField options={options?.filter(option => option?.ZUID !== currentItem?.ZUID)} loading={isLoading} value={targetInternal} defaultValue={targetPath} onChange={setTargetInternal} onSearch={handleSearch} /> : <PathField testId="RedirectsExternalFieldPath" placeHolder={t("content.redirectEnterUrlPlaceholder")} value={targetPath} onChange={value => {
                setTargetPath(value);
              }} validation={targetType === "external" ? urlValidation : null} />}
              </FieldWrapper>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{
      px: 2.5,
      pb: 2.5,
      pt: 3.75
    }} data-sentry-element="DialogActions" data-sentry-source-file="ContentRedirectModal.tsx">
        <Button variant="text" color="inherit" onClick={onClose} data-sentry-element="Button" data-sentry-source-file="ContentRedirectModal.tsx">
          {t("content.redirectDontCreate")}
        </Button>
        <Button data-cy="RedirectContentItemConfirmButton" variant="contained" color="primary" onClick={handleCreateRedirect} loading={isRedirectsLoading} disabled={isRedirectsLoading || invalidTarget || !target} data-sentry-element="Button" data-sentry-source-file="ContentRedirectModal.tsx">
          {t("content.redirectCreateButton")}
        </Button>
      </DialogActions>
    </Dialog>;
};